import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, PenLine, Pencil, Trash2, Check, X, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, moodLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

const ReflectionItem = ({ item, onSaved, onDeleted, hasTherapist }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.reflection);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    setBusy(true);
    try {
      const r = await api.patch(`/practice/${item.id}/reflection`, { reflection: value });
      onSaved(r.data);
      setEditing(false);
      toast.success("Reflection updated");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.patch(`/practice/${item.id}/reflection`, { reflection: "" });
      onDeleted(item.id);
      toast.success("Reflection removed");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const toggleShare = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/practice/${item.id}/share`, { shared: !item.reflection_shared });
      onSaved(r.data);
      toast.success(r.data.reflection_shared ? "Shared with your therapist" : "No longer shared");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <li className="relative pl-8 pb-8 last:pb-0" data-testid={`reflection-item-${item.id}`}>
      <span className="absolute left-0 top-1.5 w-3 h-3 rounded-full bg-indigo-500 ring-4 ring-indigo-100" />
      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
        {fmtDate(item.local_date)}
        {item.reflection_shared && <span className="inline-flex items-center gap-1 normal-case tracking-normal font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5" data-testid={`reflection-shared-badge-${item.id}`}><Share2 className="w-3 h-3" /> Shared with therapist</span>}
      </p>
      <p className="text-xs text-slate-500 mt-0.5">
        <Link to={`/tutorials/${item.tutorial_id}`} className="hover:text-indigo-700 font-medium">{item.tutorial_title}</Link>
        {" · "}{item.minutes} min{item.mood_before && item.mood_after ? ` · ${moodLabel(item.mood_before)} → ${moodLabel(item.mood_after)}` : ""}
      </p>
      {editing ? (
        <form onSubmit={save} className="flex gap-2 mt-3">
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} className="rounded-xl bg-white" autoFocus data-testid={`reflection-edit-input-${item.id}`} />
          <Button type="submit" size="sm" disabled={busy} className="rounded-xl" data-testid={`reflection-edit-save-${item.id}`}><Check className="w-4 h-4" /></Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => { setEditing(false); setText(item.reflection); }} className="rounded-xl" data-testid={`reflection-edit-cancel-${item.id}`}><X className="w-4 h-4" /></Button>
        </form>
      ) : (
        <div className="group flex items-start gap-3 mt-2">
          <p className="font-display text-lg sm:text-xl text-slate-800 leading-snug flex-1" data-testid={`reflection-text-${item.id}`}>“{item.reflection}”</p>
          <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
            {hasTherapist && (
              <button type="button" onClick={toggleShare} disabled={busy} className={`w-8 h-8 rounded-lg grid place-items-center ${item.reflection_shared ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"}`} aria-label={item.reflection_shared ? "Stop sharing with therapist" : "Share with therapist"} title={item.reflection_shared ? "Stop sharing with therapist" : "Share with therapist"} data-testid={`reflection-share-${item.id}`}><Share2 className="w-4 h-4" /></button>
            )}
            <button type="button" onClick={() => setEditing(true)} className="w-8 h-8 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Edit reflection" data-testid={`reflection-edit-${item.id}`}><Pencil className="w-4 h-4" /></button>
            <button type="button" onClick={remove} disabled={busy} className="w-8 h-8 rounded-lg grid place-items-center text-slate-500 hover:bg-rose-50 hover:text-rose-600" aria-label="Delete reflection" data-testid={`reflection-delete-${item.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </li>
  );
};

export default function Reflections() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/practice/reflections").then((r) => setItems(r.data)).catch((e) => { toast.error(errorMessage(e)); setItems([]); });
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16" data-testid="reflections-page">
      <Link to="/my-learning" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-700 mb-6" data-testid="reflections-back-link"><ArrowLeft className="w-4 h-4" /> My Learning</Link>
      <p className="eyebrow">Reflection journal</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-2">Your own words</h1>
      <p className="text-base md:text-lg text-slate-600 mt-3 mb-12">Every reflection you have written after a practice session, newest first. Private by default — share one with your therapist only when you want to talk about it.</p>

      {!items ? (
        <Skeleton className="h-48 rounded-3xl" />
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="reflections-empty">
          <PenLine className="w-6 h-6 mx-auto mb-3 text-indigo-400" />
          No reflections yet. After your next breathing session you&apos;ll be invited to write one line about how it felt.
          <div className="mt-4"><Link to="/" className="text-indigo-600 font-semibold hover:underline">Browse the library</Link></div>
        </div>
      ) : (
        <ol className="relative border-l-2 border-indigo-100 ml-1.5" data-testid="reflections-timeline">
          {items.map((it) => (
            <ReflectionItem
              key={it.id}
              item={it}
              hasTherapist={!!user?.therapist_id}
              onSaved={(r) => setItems((list) => list.map((x) => (x.id === r.id ? { ...x, reflection: r.reflection, reflection_shared: !!r.reflection_shared } : x)))}
              onDeleted={(id) => setItems((list) => list.filter((x) => x.id !== id))}
            />
          ))}
        </ol>
      )}
    </div>
  );
}
